class BenefitsCarousel extends HTMLElement {
  constructor() {
    super();
    
    this.currentIndex = 0;
    this.isPaused = false;
    this.autoplayTimer = null;
    this.progressTimer = null;
    this.startTime = null;
    this.remainingTime = null;
    
    this.viewport = this.querySelector('.benefits-carousel__viewport');
    this.track = this.querySelector('.benefits-carousel__track');
    this.items = Array.from(this.querySelectorAll('.benefits-carousel__item'));
    this.dividers = Array.from(this.querySelectorAll('.benefits-carousel__divider'));
    this.prevButton = this.querySelector('.benefits-carousel__arrow--prev');
    this.nextButton = this.querySelector('.benefits-carousel__arrow--next');
    this.progressBar = this.querySelector('.benefits-carousel__progress-bar');
    
    this.itemsDesktop = parseInt(this.dataset.itemsDesktop) || 4;
    this.itemsMobile = parseInt(this.dataset.itemsMobile) || 1;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) * 1000 || 5000;
    this.showArrows = this.dataset.showArrows === 'true';
    
    this.currentItemsVisible = this.getItemsVisible();
    // Total de slides é o número total de items (não dividido)
    this.totalSlides = this.items.length;
  }
  
  connectedCallback() {
    this.init();
    this.setupEventListeners();
    this.startAutoplay();
    this.updateLayout();
    
    // Garantir que o primeiro item seja ativo no mobile
    requestAnimationFrame(() => {
      if (this.items.length > 0 && this.getItemsVisible() === 1) {
        this.items[0].classList.add('is-active');
      }
    });
  }
  
  disconnectedCallback() {
    this.stopAutoplay();
    window.removeEventListener('resize', this.handleResize);
  }
  
  init() {
    // Inicializar primeiro item como ativo (para fade no mobile)
    if (this.items.length > 0) {
      this.items[0].classList.add('is-active');
    }
    
    this.updateLayout();
    this.updateArrowsState();
  }
  
  setupEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }
    
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    
    this.handleResize = this.debounce(() => {
      const newItemsVisible = this.getItemsVisible();
      if (newItemsVisible !== this.currentItemsVisible) {
        this.currentItemsVisible = newItemsVisible;
        const maxIndex = this.totalSlides - newItemsVisible;
        this.currentIndex = Math.min(this.currentIndex, maxIndex);
        this.updateLayout();
        this.updateArrowsState();
      }
    }, 250);
    
    window.addEventListener('resize', this.handleResize);
  }
  
  getItemsVisible() {
    return window.innerWidth < 750 ? this.itemsMobile : this.itemsDesktop;
  }
  
  updateLayout() {
    const itemsVisible = this.getItemsVisible();
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const viewportWidth = this.viewport.offsetWidth;
    
    // Calcular largura dos items
    // Items devem ocupar o espaço disponível igualmente
    // Divisores têm largura mínima (auto)
    const totalGaps = (itemsVisible - 1) * gap; // gaps entre items visíveis
    const itemWidth = (viewportWidth - totalGaps) / itemsVisible;
    
    // Aplicar largura aos items
    this.items.forEach(item => {
      item.style.width = `${itemWidth}px`;
    });
    
    // Divisores têm largura automática (conteúdo)
    this.dividers.forEach(divider => {
      divider.style.width = 'auto';
    });
    
    this.updateSlidePosition();
  }
  
  updateSlidePosition() {
    const itemsVisible = this.getItemsVisible();
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    
    // Mobile: usar fade (opacity) ao invés de slide
    if (itemsVisible === 1) {
      this.items.forEach((item, index) => {
        if (index === this.currentIndex) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }
      });
      return;
    }
    
    // Desktop: usar slide (transform)
    let offset = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      // Cada item
      offset += this.items[i].offsetWidth;
      
      // Gap após o item (se não for o último antes do currentIndex)
      if (i < this.currentIndex) {
        offset += gap;
      }
      
      // Divisor após o item (se existir)
      const dividerIndex = i;
      if (this.dividers[dividerIndex]) {
        offset += this.dividers[dividerIndex].offsetWidth;
        offset += gap;
      }
    }
    
    this.track.style.transform = `translateX(-${offset}px)`;
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlidePosition();
      this.updateArrowsState();
      this.resetAutoplay();
    }
  }
  
  next() {
    const itemsVisible = this.getItemsVisible();
    const maxIndex = this.totalSlides - itemsVisible;
    
    // Se só há 1 item visível (mobile), navegar item por item
    if (itemsVisible === 1) {
      if (this.currentIndex < this.totalSlides - 1) {
        this.currentIndex++;
      } else {
        // Voltar ao início
        this.currentIndex = 0;
      }
    } else {
      // Desktop: navegação normal
      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
      } else {
        // Voltar ao início
        this.currentIndex = 0;
      }
    }
    
    this.updateSlidePosition();
    this.updateArrowsState();
    this.resetAutoplay();
  }
  
  updateArrowsState() {
    if (!this.showArrows) return;
    
    const itemsVisible = this.getItemsVisible();
    const maxIndex = this.totalSlides - itemsVisible;
    
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    
    if (this.nextButton) {
      this.nextButton.disabled = false; // Sempre habilitado pois faz loop
    }
  }
  
  startAutoplay() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    
    this.stopAutoplay();
    this.startTime = Date.now();
    this.remainingTime = this.autoplaySpeed;
    
    this.autoplayTimer = setTimeout(() => {
      this.next();
      this.startAutoplay();
    }, this.remainingTime);
    
    this.startProgress();
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    
    if (this.progressTimer) {
      cancelAnimationFrame(this.progressTimer);
      this.progressTimer = null;
    }
    
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
    }
  }
  
  pause() {
    if (this.isPaused) return;
    
    this.isPaused = true;
    const elapsed = Date.now() - this.startTime;
    this.remainingTime = this.autoplaySpeed - elapsed;
    
    this.stopAutoplay();
  }
  
  resume() {
    if (!this.isPaused) return;
    
    this.isPaused = false;
    this.startAutoplay();
  }
  
  resetAutoplay() {
    this.stopAutoplay();
    this.isPaused = false;
    this.startAutoplay();
  }
  
  startProgress() {
    if (!this.progressBar) return;
    
    const startTime = Date.now();
    const duration = this.remainingTime;
    
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      
      this.progressBar.style.width = `${progress}%`;
      this.progressBar.style.transitionDuration = '0s';
      
      if (progress < 100 && !this.isPaused) {
        this.progressTimer = requestAnimationFrame(updateProgress);
      }
    };
    
    this.progressTimer = requestAnimationFrame(updateProgress);
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('benefits-carousel', BenefitsCarousel);

